import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";

const PDF_ENDPOINT = "/FillMyForm/BOM_AADHAAR_LINKING/Print.php";
const STORAGE_KEY = "smart-solutions-bom-aadhaar-linking";

const applicantFields = [
  { name: "branch", label: "Branch Name", maxLength: 11, required: true },
  { name: "acno", label: "Account Number", maxLength: 11, numeric: true, account: true, required: true },
  { name: "name", label: "Applicant Name", maxLength: 40, alphaOnly: true, required: true },
  { name: "aadhaar", label: "Aadhaar Number", maxLength: 12, numeric: true, aadhaar: true, required: true },
  { name: "mobile", label: "Mobile Number", maxLength: 10, numeric: true, mobile: true, required: true },
];

const initialFormData = Object.fromEntries(applicantFields.map((field) => [field.name, ""]));
const tabs = [
  { title: "Applicant", heading: "👤 Applicant Details", fields: applicantFields },
  { title: "Preview", heading: "✅ Preview", fields: [], preview: true },
];

function validateField(field, value) {
  const text = String(value ?? "").trim();
  if (field.required && !text) return `${field.label} is required.`;
  if (!text) return "";
  if (field.alphaOnly && !/^[A-Za-z ]+$/.test(text)) {
    return `${field.label} must contain alphabet letters only.`;
  }
  if (field.account && !/^\d{11}$/.test(text)) return "Enter a valid 11-digit account number.";
  if (field.aadhaar && !/^\d{12}$/.test(text)) return "Aadhaar number must contain exactly 12 digits.";
  if (field.mobile && !/^[6-9][0-9]{9}$/.test(text)) return "Enter a valid 10-digit mobile number.";
  return "";
}

export default function AadhaarLinking() {
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [formData, setFormData] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      return saved ? { ...initialFormData, ...JSON.parse(saved) } : initialFormData;
    } catch {
      return initialFormData;
    }
  });
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);

  const visibleFields = tabs[currentTab].fields;
  const firstFieldName = visibleFields[0]?.name || "";
  const lastFieldName = visibleFields.at(-1)?.name || "";

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);

  useEffect(() => {
    if (!firstFieldName) return undefined;
    const frameId = window.requestAnimationFrame(() => document.getElementById(firstFieldName)?.focus());
    return () => window.cancelAnimationFrame(frameId);
  }, [currentTab, firstFieldName]);

  useEffect(() => {
    const controller = new AbortController();
    const loadAutofill = async () => {
      try {
        const response = await fetch("/resources/ajax_autofill.php", {
          method: "POST",
          body: new URLSearchParams({ autofill: "BOM_AADHAAR_LINKING" }),
          credentials: "include",
          cache: "no-store",
          headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
          signal: controller.signal,
        });
        if (!response.ok) return;
        const result = await response.json();
        if (!Array.isArray(result) || !result[0]) return;
        setFormData((previous) => ({ ...previous, branch: previous.branch || result[0].branch || "" }));
      } catch (error) {
        if (error.name !== "AbortError") console.error("BOM Aadhaar Linking autofill error:", error);
      }
    };
    loadAutofill();
    return () => controller.abort();
  }, []);

  const updateValue = (field, rawValue) => {
    let value = rawValue;

    if (field.numeric) {
      value = value.replace(/\D/g, "");
    }

    if (field.alphaOnly) {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    setFormData((previous) => ({ ...previous, [field.name]: value }));
    setErrors((previous) => ({ ...previous, [field.name]: "" }));
    setPdfError("");
  };

  const validateFields = (fields) => {
    const nextErrors = {};
    fields.forEach((field) => {
      const message = validateField(field, formData[field.name]);
      if (message) nextErrors[field.name] = message;
    });
    setErrors((previous) => ({ ...previous, ...nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    if (!validateFields(visibleFields)) return;
    setCurrentTab(1);
    setMaxAllowedTab(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async () => {
    if (!validateFields(applicantFields)) {
      setPdfError("Please correct the highlighted fields before submitting.");
      return;
    }
    setPdfLoading(true);
    setPdfError("");
    try {
      const postData = new FormData();
      Object.entries(formData).forEach(([name, value]) => postData.append(name, String(value ?? "")));
      const response = await fetch(PDF_ENDPOINT, {
        method: "POST",
        body: postData,
        credentials: "include",
        cache: "no-store",
        headers: { Accept: "application/pdf" },
      });
      const contentType = response.headers.get("content-type") || "";
      if (!response.ok || !contentType.toLowerCase().includes("application/pdf")) {
        const responseText = await response.text();
        throw new Error(responseText.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || `PDF generation failed. HTTP status: ${response.status}`);
      }
      const objectUrl = URL.createObjectURL(await response.blob());
      setPdfUrl((previousUrl) => {
        if (previousUrl?.startsWith("blob:")) URL.revokeObjectURL(previousUrl);
        return objectUrl;
      });
      setShowPdf(true);
    } catch (error) {
      setPdfError(error.message || "Unable to generate PDF preview.");
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => (
    <div key={field.name} className="space-y-2">
      <Label htmlFor={field.name}>{field.label}<span className="text-red-500"> *</span></Label>
      <Input
        id={field.name}
        name={field.name}
        value={formData[field.name]}
        placeholder={field.label}
        maxLength={field.maxLength}
        inputMode={field.numeric ? "numeric" : undefined}
        onChange={(event) => updateValue(field, event.target.value)}
        onKeyDown={(event) => {
          if (field.name === lastFieldName && event.key === "Tab" && !event.shiftKey) {
            event.preventDefault();
            nextButtonRef.current?.focus();
          }
        }}
        className={errors[field.name] ? "border-red-500" : ""}
        autoComplete="on"
      />
      {errors[field.name] && <p className="text-sm text-red-500">{errors[field.name]}</p>}
    </div>
  );

  if (showPdf && pdfUrl) {
    return <PdfViewerContainer pdfUrl={pdfUrl} title="BOM Aadhaar Linking Form" backRoute="/Fill_My_Form" onBack={() => setShowPdf(false)} />;
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">🏦 Bank of Maharashtra Aadhaar Linking Form</CardTitle>
            <p className="text-muted-foreground">Fill account and Aadhaar details carefully.</p>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-2 gap-3 mb-8">
          {tabs.map((tab, index) => (
            <Button key={tab.title} type="button" variant={currentTab === index ? "default" : "outline"} disabled={index > maxAllowedTab || pdfLoading} onClick={() => setCurrentTab(index)}>
              {index + 1}. {tab.title}
            </Button>
          ))}
        </div>

        {currentTab === 0 && (
          <Card>
            <CardHeader><CardTitle>👤 Applicant Details</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{applicantFields.map(renderField)}</div>
            </CardContent>
          </Card>
        )}

        {currentTab === 1 && (
          <Card>
            <CardHeader><CardTitle>✅ Preview</CardTitle></CardHeader>
            <CardContent>
              <PreviewSection rows={applicantFields.map((field) => [field.label, formData[field.name]])} />
            </CardContent>
          </Card>
        )}

        {pdfLoading && <div className="mt-6 text-center">Generating PDF, please wait...</div>}
        {pdfError && <div className="mt-6 rounded-lg border border-red-500 p-4 text-red-500">{pdfError}</div>}

        <div className="flex justify-between mt-8">
          <Button type="button" variant="outline" onClick={() => setCurrentTab(0)} disabled={currentTab === 0 || pdfLoading}>← Previous</Button>
          {currentTab === 1 ? (
            <Button type="button" onClick={handleSubmit} disabled={pdfLoading}>{pdfLoading ? "Generating PDF..." : "Submit"}</Button>
          ) : (
            <Button ref={nextButtonRef} type="button" onClick={goNext} disabled={pdfLoading}>Preview</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewSection({ rows }) {
  return (
    <div className="rounded-2xl border p-6">
      <h3 className="text-xl font-semibold text-center mb-6">Applicant Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map(([label, value]) => (
          <div key={label} className="grid grid-cols-2 gap-3 border-b pb-2">
            <div className="font-medium text-muted-foreground">{label}</div>
            <div>{value || "-"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
